// De kluis: notities die de NAS zelf niet kan lezen.
//
// Er staan wachtwoorden in notities. De bescherming was tot nu toe Authentik plus de schijf van
// de NAS — genoeg tegen iemand op je netwerk, niets tegen iemand die de schijf in handen krijgt
// of een nachtelijke export meeneemt. Een kluisnotitie wordt daarom in de browser versleuteld
// en pas daar weer ontsleuteld. De server bewaart alleen een blok bytes.
//
// Dat is een echte keuze met een echte prijs, en die staat hier zodat niemand hem later per
// ongeluk terugdraait:
//
//   * **Wij kunnen niets terughalen.** Het wachtwoord staat nergens; kwijt is kwijt, ook met
//     een back-up erbij. Daarom vraagt het scherm er bij het instellen twee keer om en zegt het
//     dit met zoveel woorden.
//   * **De server kan er niet in zoeken.** Geen zoekindex, geen taken, geen eigenschappen uit
//     een kluisnotitie — dat zou de inhoud alsnog leesbaar op de NAS zetten.
//   * **De titel blijft leesbaar.** Anders kun je je eigen notitie niet meer terugvinden in de
//     lijst. Zet dus geen wachtwoord in de titel; het scherm zegt dat er ook bij.
//
// De techniek is met opzet saai en standaard, zodat je hem over tien jaar nog kunt naspelen:
// PBKDF2-SHA256 om van je wachtwoord een sleutel te maken, AES-GCM om de tekst te versleutelen.
// Allebei zitten ze in elke browser (WebCrypto) en in Node, dus er komt geen bibliotheek aan te
// pas die over drie jaar niet meer wordt onderhouden. De parameters gaan mee in de notitie én
// in de export: zout, aantal rondes en de iv staan er leesbaar bij, zodat een los scriptje het
// ook kan als deze app er niet meer is.

import type { Blok } from "./api.ts";

/**
 * Zoveel rondes doet PBKDF2. De aanbeveling van OWASP voor PBKDF2-SHA256 (2023) is 600.000;
 * dat kost op een laptop ongeveer een halve seconde. Je doet het één keer per keer dat je de
 * kluis opent, dus dat mag het kosten — voor wie je wachtwoord wil raden is het alles.
 */
export const RONDES = 600_000;

/** Hiermee controleren we of het wachtwoord klopt; de inhoud doet er niet toe. */
const CONTROLEWOORD = "notities-kluis-v1";

export type Envelop = {
  v: number;
  alg: "AES-GCM";
  kdf: "PBKDF2-SHA256";
  rondes: number;
  zout: string;
  iv: string;
  data: string;
};

function naarBase64(bytes: ArrayBuffer | Uint8Array): string {
  const rij = bytes instanceof Uint8Array ? bytes : new Uint8Array(bytes);
  let tekst = "";
  for (const byte of rij) tekst += String.fromCharCode(byte);
  return btoa(tekst);
}

function uitBase64(tekst: string): Uint8Array {
  const rauw = atob(tekst);
  const bytes = new Uint8Array(rauw.length);
  for (let i = 0; i < rauw.length; i++) bytes[i] = rauw.charCodeAt(i);
  return bytes;
}

export function maakZout(): string {
  return naarBase64(crypto.getRandomValues(new Uint8Array(16)));
}

/** Van wachtwoord naar sleutel. Duurt expres lang; zie RONDES. */
export async function afgeleideSleutel(wachtwoord: string, zout: string, rondes = RONDES): Promise<CryptoKey> {
  const basis = await crypto.subtle.importKey("raw", new TextEncoder().encode(wachtwoord), "PBKDF2", false, [
    "deriveKey"
  ]);
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", salt: uitBase64(zout) as BufferSource, iterations: rondes, hash: "SHA-256" },
    basis,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}

async function versleutelTekst(sleutel: CryptoKey, tekst: string, zout: string, rondes: number): Promise<Envelop> {
  // Elke keer een nieuwe iv: twee keer dezelfde met dezelfde sleutel maakt AES-GCM stuk.
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const data = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, sleutel, new TextEncoder().encode(tekst));
  return { v: 1, alg: "AES-GCM", kdf: "PBKDF2-SHA256", rondes, zout, iv: naarBase64(iv), data: naarBase64(data) };
}

async function ontsleutelTekst(sleutel: CryptoKey, envelop: Envelop): Promise<string> {
  const bytes = await crypto.subtle.decrypt(
    { name: "AES-GCM", iv: uitBase64(envelop.iv) as BufferSource },
    sleutel,
    uitBase64(envelop.data) as BufferSource
  );
  return new TextDecoder().decode(bytes);
}

/**
 * Een notitie-inhoud als versleuteld blok.
 *
 * Het blijft een geldig document met één knoop erin, zodat alles eromheen — de database, de
 * wachtrij, de spiegel — er niets van hoeft te weten. Wat erin zit is de echte inhoud.
 */
export async function versleutel(sleutel: CryptoKey, doc: Blok | null, zout: string, rondes = RONDES): Promise<Blok> {
  const envelop = await versleutelTekst(sleutel, JSON.stringify(doc ?? null), zout, rondes);
  return { type: "doc", content: [{ type: "kluis", attrs: envelop as unknown as Record<string, unknown> }] };
}

export function envelopVan(doc: Blok | null | undefined): Envelop | null {
  const knoop = doc?.content?.[0];
  if (!doc || doc.type !== "doc" || doc.content?.length !== 1 || knoop?.type !== "kluis") return null;
  const attrs = knoop.attrs as unknown as Envelop | undefined;
  if (!attrs?.data || !attrs.iv || !attrs.zout) return null;
  return attrs;
}

/** Zit deze notitie op slot? Dat kun je aan de inhoud zien, ook zonder sleutel. */
export function isVersleuteld(doc: Blok | null | undefined): boolean {
  return envelopVan(doc) !== null;
}

export async function ontsleutel(sleutel: CryptoKey, doc: Blok | null | undefined): Promise<Blok | null> {
  const envelop = envelopVan(doc);
  if (!envelop) throw new Error("dit is geen kluisnotitie");
  return JSON.parse(await ontsleutelTekst(sleutel, envelop)) as Blok | null;
}

/** Een blokje waarmee je later kunt controleren of een wachtwoord klopt. */
export async function maakControle(sleutel: CryptoKey, zout: string, rondes = RONDES): Promise<string> {
  return JSON.stringify(await versleutelTekst(sleutel, CONTROLEWOORD, zout, rondes));
}

export async function klopt(sleutel: CryptoKey, controle: string): Promise<boolean> {
  try {
    return (await ontsleutelTekst(sleutel, JSON.parse(controle) as Envelop)) === CONTROLEWOORD;
  } catch {
    // Een verkeerd wachtwoord geeft geen leesbare tekst maar een fout uit AES-GCM; dat ís het
    // antwoord, en niet iets om over te struikelen.
    return false;
  }
}

// --- het slot ----------------------------------------------------------------------------
//
// De sleutel staat alleen in het geheugen van dit tabblad: niet in localStorage, niet in
// IndexedDB. Sluit je de app, dan is de kluis weer dicht. Dat is het verschil tussen "mijn
// notities staan versleuteld op de NAS" en "iedereen die mijn laptop openklapt leest ze".

let sleutelInGeheugen: CryptoKey | null = null;
let klok: ReturnType<typeof setTimeout> | null = null;
const kijkers = new Set<(open: boolean) => void>();

/** Na zoveel stilte gaat de kluis vanzelf weer op slot. */
export const SLOTTIJD = 15 * 60 * 1000;

function meld(): void {
  for (const kijker of kijkers) kijker(sleutelInGeheugen !== null);
}

export function opSlot(kijker: (open: boolean) => void): () => void {
  kijkers.add(kijker);
  return () => kijkers.delete(kijker);
}

export function ontgrendel(sleutel: CryptoKey): void {
  sleutelInGeheugen = sleutel;
  houdOpen();
  meld();
}

export function vergrendel(): void {
  sleutelInGeheugen = null;
  if (klok) clearTimeout(klok);
  klok = null;
  meld();
}

/** De teller opnieuw beginnen; elke keer dat je een kluisnotitie aanraakt. */
export function houdOpen(): void {
  if (!sleutelInGeheugen) return;
  if (klok) clearTimeout(klok);
  klok = setTimeout(() => vergrendel(), SLOTTIJD);
}

export function huidigeSleutel(): CryptoKey | null {
  return sleutelInGeheugen;
}

export function isOpen(): boolean {
  return sleutelInGeheugen !== null;
}
